/**
 * The one ticking clock for every faucet wait (the Free tokens cooldown, home's "Free tokens in ..." and your own
 * profile's): the format, ONE shared 1 s clock, and the small label that owns the redraw so nothing around it does.
 * Blockari's `shell/Countdown.tsx` (3a32e85, bd8e4c0), in vanilla DOM.
 *
 *  - The shared clock ticks only while at least one countdown is mounted, pauses while the tab is hidden, and reads
 *    the time at once when the tab comes back, so nothing shows stale.
 *  - Only the label's text is redrawn each second: the minutes' text only when the minute turns, the seconds'
 *    every tick (a fresh element, so the soft pulse in ui.css restarts).
 *  - Each digit sits in a fixed 1ch cell on top of tabular-nums: the display face has no tabular figures, so without
 *    the cells the clock (and the button holding it) would change width as the digits change.
 *  - At zero the owner is asked to read the faucet again (`onElapsed`), and again every 5 s while the wait is still
 *    on screen (a server clock a little behind). The owner stops it by moving the target (`setTarget`) or by
 *    destroying the countdown once the claim is open.
 */

const SEC = 1000;
const p2 = (n: number): string => String(n).padStart(2, '0');

/**
 * The wait split for display: everything before the seconds ("4d 21h 14m ", or "" under a minute) and the seconds
 * themselves ("09s"). Leading zero units are dropped; once a larger unit is shown the smaller ones are all there,
 * with minutes and seconds zero-padded. Rounded up to the second, so the last second reads 01s and a spent wait 00s.
 */
export function countdownParts(ms: number): { lead: string; sec: string } {
  const total = Math.max(0, Math.ceil(ms / SEC));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = `${p2(total % 60)}s`;
  if (d > 0) return { lead: `${d}d ${h}h ${p2(m)}m `, sec };
  if (h > 0) return { lead: `${h}h ${p2(m)}m `, sec };
  if (m > 0) return { lead: `${m}m `, sec };
  return { lead: '', sec };
}

/** "4d 21h 14m 09s", "21h 14m 09s", "14m 09s", "09s". */
export function formatCountdown(ms: number): string {
  const { lead, sec } = countdownParts(ms);
  return lead + sec;
}

/* ------------------------------------------------------------------ the clock -- */

export interface ClockEnv {
  setInterval: (fn: () => void, ms: number) => unknown;
  clearInterval: (id: unknown) => void;
  isHidden: () => boolean;
  /** Calls `fn` when the tab's visibility changes; returns the way to stop listening. */
  onVisibility: (fn: () => void) => () => void;
}

const browserEnv: ClockEnv = {
  setInterval: (fn, ms) => globalThis.setInterval(fn, ms),
  clearInterval: (id) => globalThis.clearInterval(id as ReturnType<typeof setInterval>),
  isHidden: () => typeof document !== 'undefined' && document.hidden,
  onVisibility: (fn) => {
    if (typeof document === 'undefined') return () => undefined;
    document.addEventListener('visibilitychange', fn);
    return () => document.removeEventListener('visibilitychange', fn);
  },
};

/**
 * A 1 s clock that runs only while somebody listens and the page is visible. `subscribe` returns the way to stop
 * listening; the last one out stops the interval and the visibility listener. Coming back from hidden ticks every
 * listener at once (the catch-up) before the interval resumes.
 */
export class SharedClock {
  readonly #subs = new Set<() => void>();
  #timer: unknown = null;
  #stopVisibility: (() => void) | null = null;

  constructor(private readonly env: ClockEnv = browserEnv) {}

  /** Listeners right now (tests read it). */
  get listeners(): number {
    return this.#subs.size;
  }

  /** True while the interval is running (tests read it). */
  get running(): boolean {
    return this.#timer !== null;
  }

  subscribe(fn: () => void): () => void {
    this.#subs.add(fn);
    if (this.#subs.size === 1) {
      this.#stopVisibility = this.env.onVisibility(() => (this.env.isHidden() ? this.#pause() : this.#resume()));
      if (!this.env.isHidden()) this.#resume(false);
    }
    return () => {
      if (!this.#subs.delete(fn) || this.#subs.size > 0) return;
      this.#pause();
      this.#stopVisibility?.();
      this.#stopVisibility = null;
    };
  }

  #tick = (): void => {
    for (const fn of [...this.#subs]) fn();
  };

  #resume(catchUp = true): void {
    if (this.#timer !== null) return;
    // Back from hidden: every label reads the clock now, then the interval carries on.
    if (catchUp) this.#tick();
    this.#timer = this.env.setInterval(this.#tick, SEC);
  }

  #pause(): void {
    if (this.#timer !== null) this.env.clearInterval(this.#timer);
    this.#timer = null;
  }
}

/** The one every countdown on the page shares. */
export const sharedClock = new SharedClock();

/* ----------------------------------------------------------- the elapsed loop -- */

/** While a spent wait is still on screen it asks again this often, in ticks (seconds). */
export const RETRY_TICKS = 5;

/**
 * What a countdown does at zero: ask the owner to read the faucet again straight away, then again every
 * RETRY_TICKS ticks for as long as the spent wait stays on screen. Moving the target (`reset`) starts it over; a
 * countdown that is destroyed simply stops being ticked.
 */
export class ElapsedLoop {
  #spent = 0;

  constructor(private readonly ask: () => void) {}

  /** One tick with `left` ms to go. */
  tick(left: number): void {
    if (left > 0) {
      this.#spent = 0;
      return;
    }
    if (this.#spent % RETRY_TICKS === 0) this.ask();
    this.#spent += 1;
  }

  reset(): void {
    this.#spent = 0;
  }
}

/**
 * A countdown's state without a DOM: the target, and the loop at zero. `tick` returns what to show.
 * Moving the target to the SAME time (the server still says the wait ends when it said) changes nothing, so the
 * retries stay five seconds apart instead of following each read; a later time starts the loop over.
 */
export class CountdownCore {
  readonly #loop: ElapsedLoop;

  constructor(
    public to: number,
    ask: () => void,
  ) {
    this.#loop = new ElapsedLoop(ask);
  }

  tick(now: number): { lead: string; sec: string } {
    const left = this.to - now;
    this.#loop.tick(left);
    return countdownParts(left);
  }

  /** True when the target moved (and the loop started over). */
  setTarget(next: number): boolean {
    if (next === this.to) return false;
    this.to = next;
    this.#loop.reset();
    return true;
  }
}

/* ------------------------------------------------------------------ the label -- */

/** The text with each digit in its own fixed-width cell (see the header). */
function digits(text: string): Node[] {
  return text.split(/(\d)/).filter((part) => part !== '').map((part) => {
    if (!/^\d$/.test(part)) return document.createTextNode(part);
    const cell = document.createElement('span');
    cell.className = 'w21-cd-n';
    cell.textContent = part;
    return cell;
  });
}

export interface Countdown {
  /** `<span class="w21-cd">`: put it where the clock goes. */
  readonly root: HTMLElement;
  /** Count down to a new time (epoch ms): the owner read the faucet again and the wait moved. */
  setTarget(to: number): void;
  destroy(): void;
}

/**
 * "4d 21h 14m 09s" counting down to `to` (epoch ms), driven by the shared clock. Only this label is redrawn.
 * `onElapsed` fires at zero and every RETRY_TICKS seconds after, until the owner moves the target or destroys it.
 */
export function mountCountdown(opts: { to: number; onElapsed?: () => void; clock?: SharedClock; now?: () => number }): Countdown {
  const clock = opts.clock ?? sharedClock;
  const now = opts.now ?? Date.now;
  const core = new CountdownCore(opts.to, () => opts.onElapsed?.());
  const root = document.createElement('span');
  root.className = 'w21-cd';
  let lead = '';
  const leadEl = document.createElement('span');
  let secEl = document.createElement('span');
  root.append(leadEl, secEl);

  const paint = (): void => {
    const parts = core.tick(now());
    // The minutes' text only when the minute turns; the seconds' every tick, in a fresh element so the pulse restarts.
    if (parts.lead !== lead) {
      lead = parts.lead;
      leadEl.replaceChildren(...digits(lead));
    }
    const next = document.createElement('span');
    next.className = 'w21-cd-sec';
    next.append(...digits(parts.sec));
    secEl.replaceWith(next);
    secEl = next;
  };
  paint();
  const stop = clock.subscribe(paint);

  return {
    root,
    setTarget(next) {
      if (core.setTarget(next)) paint();
    },
    destroy() {
      stop();
      root.remove();
    },
  };
}
